import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Observable, from, map, of, switchMap } from 'rxjs';
import { Participation } from '../entities/Participation.js';
import { BHFilter } from '../entities/bh-filter.js';
import { Product } from '../entities/products.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { User } from '../entities/user.js';

@Entity()
export class ExportParticipation extends AdornisEntity {
  static override _class = 'ExportParticipation';

  static resolveID(entity) {
    return entity._id;
  }

  static fieldBlacklist = [
    'user.roles',
    'user.isOnline',
    'user.ios_sub',
    'user.participations',
    'user.isLdapAccount',
    'user.stripeCustomerID',
    'participation.product',
    'participation.timeslotID',
    'participation.beraterID',
    'participation.user',
    'product.reference',
    'product.prices.interval',
    'product.prices.intervalCount',
    'product.prices.stripeID',
    'product.prices.bundleID',
    'product.settings.productID',
    'product.settings.parentInstitutionID',
    'product.beraterAvailabilities',
    'product.participations',
    // /IDs?$/,
    /_class$/,
  ];

  @Field(type => ID) _id!: string;
  @Field(type => User) user: Maybe<User>;
  @Field(type => Participation) participation: Maybe<Participation>;
  @Field(type => Product) product: Maybe<Product>;
  @Field(type => UserStammdaten) stammdaten: Maybe<UserStammdaten>;
}

export const subscribeExportDataView = registerSubscription({
  operationName: 'subscribeExportDataView',
  type: () => [ExportParticipation],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve:
    (skip: number, limit: number, filter: BHFilter) =>
    (gqlFields: BaseQLSelectionSet<ExportParticipation>): Observable<ExportParticipation[]> => {
      if (!filter.institutionID) return of([] as ExportParticipation[]);
      return from(publishFilter(Participation._class)).pipe(
        switchMap(pF => {
          if (!pF) throw new Error('No publishfilter defined for Participation in ExportDataView.getExportDataView');
          return getCollectionHandle(ExportParticipation._class)
            .watchQuery(pF)
            .pipe(map(() => pF));
        }),
        switchMap(async pF => {
          const col = await getRawCollection<EntityData<Participation>>(Participation._collectionName);
          return col
            .aggregate([
              { $match: pF },
              {
                $replaceRoot: {
                  newRoot: { participation: '$$ROOT', _class: ExportParticipation._class, _id: '$$ROOT._id' },
                },
              },
              {
                $lookup: {
                  from: UserStammdaten._collectionName,
                  let: { userID: '$participation.userID' },
                  pipeline: [
                    {
                      $match: {
                        $expr: {
                          $and: [{ $eq: ['$$userID', '$userID'] }, { $eq: ['$institutionID', filter.institutionID] }],
                        },
                      },
                    },
                  ],
                  as: 'stammdaten',
                },
              },
              { $set: { stammdaten: { $arrayElemAt: ['$stammdaten', 0] } } },
              {
                $lookup: {
                  from: User._collectionName,
                  as: 'user',
                  localField: 'participation.userID',
                  foreignField: '_id',
                },
              },
              { $unwind: '$user' },
              {
                $lookup: {
                  from: Product._collectionName,
                  as: 'product',
                  localField: 'participation.productID',
                  foreignField: '_id',
                },
              },
              { $unwind: '$product' },
              { $match: { $and: [filter.getMongoFilter(), { 'product.institutionID': filter.institutionID }] } },
              { $sort: filter.getMongoOptions().sort },
              { $skip: skip },
              { $limit: limit },
            ])
            .toArray() as Promise<ExportParticipation[]>;
        }),
      );
    },
});
